import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, useScroll, useMotionValueEvent, AnimatePresence, useTransform } from 'framer-motion';

/* ── Tilt Card ── */
function TiltCard({ children, className, style, ...props }) {
  const ref = useRef(null);
  const handleMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 16;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -16;
    ref.current.style.transform = `perspective(1000px) rotateY(${x}deg) rotateX(${y}deg) translateZ(8px)`;
  };
  const handleLeave = () => {
    ref.current.style.transform = 'perspective(1000px) rotateY(0) rotateX(0) translateZ(0)';
  };
  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className={`tilt-card ${className || ''}`}
      style={{ transition: 'transform 0.15s ease-out', ...style }}
      {...props}
    >
      {children}
    </div>
  );
}

/* ── Reveal on scroll ── */
function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

/* ── Waveform animation ── */
function LiveWaveform() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: 40 }}>
      {Array.from({ length: 24 }, (_, i) => (
        <div
          key={i}
          className="wave-bar"
          style={{
            width: 3,
            height: `${20 + Math.sin(i * 0.8) * 14}px`,
            background: '#C8922A',
            borderRadius: 2,
            '--dur': `${0.6 + (i % 5) * 0.15}s`,
            animationDelay: `${i * 0.06}s`,
          }}
        />
      ))}
    </div>
  );
}

/* ── Web Audio Context for Swaras ── */
let audioCtx = null;
function playSwara(freq) {
  if (typeof window === 'undefined') return;
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === 'suspended') audioCtx.resume();

  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  
  // Rich, ethereal bell/sine sound
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, audioCtx.currentTime);
  
  // Envelope: gentle attack, long atmospheric release
  gain.gain.setValueAtTime(0, audioCtx.currentTime);
  gain.gain.linearRampToValueAtTime(0.4, audioCtx.currentTime + 0.1);
  gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 4.0);
  
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  
  osc.start();
  osc.stop(audioCtx.currentTime + 4.0);
}

/* ── Sticky Narrative: The Swara Scroll ── */
function StickyNarrative() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start start', 'end end'] });
  
  const [activeStage, setActiveStage] = useState(-1);

  // Swara Configuration with Functional Business Narrative
  const swaras = [
    { note: 'Sa', hindi: 'सा', freq: 261.63, xStart: '-5vw', yStart: '100vh', xEnd: '-60vw',  yEnd: '-40vh', rotStart: -30, rotEnd: 120, color: '#FAF7F0', 
      title: 'Recording Studio', desc: 'State-of-the-art vocal & instrument tracking with premium recording microphones.', cta: 'Book Recording', link: '#book-recording' },
    { note: 'Re', hindi: 'रे',   freq: 293.66, xStart: '10vw',  yStart: '100vh', xEnd: '50vw', yEnd: '-60vh', rotStart: 45, rotEnd: -90, color: '#D4A040', 
      title: 'Vocal Classes', desc: 'Expert training in Indian Classical and Western contemporary vocal styles.', cta: 'Join Classes', link: '#vocal-classes' },
    { note: 'Ga', hindi: 'ग',   freq: 329.63, xStart: '0vw', yStart: '100vh', xEnd: '-40vw',  yEnd: '-50vh', rotStart: 15, rotEnd: 220, color: '#FAF7F0', 
      title: 'Musical Instruments', desc: 'Master the Guitar, Piano, Tabla, or Sitar with personalized 1-on-1 mentorship.', cta: 'Explore Instruments', link: '#instrument-classes' },
    { note: 'Ma', hindi: 'म',   freq: 349.23, xStart: '-10vw',  yStart: '100vh', xEnd: '60vw', yEnd: '-30vh', rotStart: -60, rotEnd: 180, color: '#E8A830', 
      title: 'Dolby Atmos Mixing', desc: 'Immersive 3D audio mixing and mastering engineered for cinema and streaming.', cta: 'Get Mixed', link: '#dolby-atmos' },
    { note: 'Pa', hindi: 'प',   freq: 392.00, xStart: '5vw', yStart: '100vh', xEnd: '-30vw',  yEnd: '-70vh', rotStart: 90, rotEnd: -120, color: '#FAF7F0', 
      title: 'Session Musicians', desc: 'Hire professional strings, brass, and classical percussionists for your tracks.', cta: 'Hire Artists', link: '#session-musicians' },
    { note: 'Dha',hindi: 'ध',   freq: 440.00, xStart: '-5vw',  yStart: '100vh', xEnd: '70vw', yEnd: '-40vh', rotStart: -15, rotEnd: 150, color: '#C8922A', 
      title: 'AI Production Assistant', desc: 'Generate lyrics, chord progressions, and studio insights using our advanced AI.', cta: 'Open AI Portal', link: '#ai-features' },
    { note: 'Ni', hindi: 'नि',  freq: 493.88, xStart: '8vw', yStart: '100vh', xEnd: '-50vw',  yEnd: '-50vh', rotStart: 60, rotEnd: -180, color: '#FAF7F0', 
      title: 'Production Courses', desc: 'Learn Ableton, Logic Pro, and complete sound design from industry professionals.', cta: 'Start Learning', link: '#production-courses' },
    { note: 'Sa', hindi: 'सां',  freq: 523.25, xStart: '-2vw',   yStart: '100vh', xEnd: '0vw',   yEnd: '-80vh', rotStart: 0, rotEnd: 360, color: '#FFD700', 
      title: 'Jam & Rehearsal Space', desc: 'Acoustically treated spaces equipped with premium backline for your band.', cta: 'Book Jam Room', link: '#rehearsal-space' },
  ];

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    let stage;
    if (latest <= 0.001) {
      stage = -1; // Scrolled above the section
    } else if (latest >= 0.999) {
      stage = swaras.length; // Scrolled past the section
    } else {
      stage = Math.floor(latest * swaras.length);
    }

    if (stage !== activeStage) {
      // Play the sound only if we are inside the 0-7 array bounds
      if (stage >= 0 && stage < swaras.length) {
        playSwara(swaras[stage].freq);
      }
      setActiveStage(stage);
    }
  });

  return (
    <div ref={containerRef} style={{ height: '800vh', position: 'relative' }}>
      <div className="sticky-section" style={{ background: '#0D0904', overflow: 'hidden' }}>
        
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at center, #1A1209 0%, #000 100%)', zIndex: 0 }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url("https://upload.wikimedia.org/wikipedia/commons/7/76/1k_Dissolve_Noise_Texture.png")', opacity: 0.05, mixBlendMode: 'overlay', pointerEvents: 'none', zIndex: 1 }} />

        {swaras.map((swara, index) => {
          const startTrigger = index / swaras.length;
          const endTrigger = (index + 2) / swaras.length;
          return <FloatSwara key={index} swara={swara} progress={scrollYProgress} start={startTrigger} end={endTrigger} isActive={activeStage === index} />;
        })}

        {/* Central Narrative that updates per active swara */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <AnimatePresence mode="wait">
            {(() => {
              const isValidStage = activeStage >= 0 && activeStage < swaras.length;
              return (
                <motion.div
                  key={activeStage}
                  initial={{ opacity: 0, y: 30, filter: 'blur(10px)', scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }}
                  exit={{ opacity: 0, y: -20, filter: 'blur(10px)', scale: 1.05 }}
                  transition={{ duration: 0.8, ease: [0.16,1,0.3,1] }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24, maxWidth: 640, padding: '0 6%' }}
                >
                  {isValidStage ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 13, letterSpacing: '4px', color: swaras[activeStage].color, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, textTransform: 'uppercase' }}>
                          {swaras[activeStage].note}
                        </span>
                        <div style={{ width: 24, height: 1, background: swaras[activeStage].color, opacity: 0.5 }} />
                        <span style={{ fontSize: 13, letterSpacing: '3px', color: '#C8922A', fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase' }}>
                          {swaras[activeStage].title}
                        </span>
                      </div>
                      
                      <h3 style={{ 
                        fontFamily: "'Cormorant Garamond',serif", 
                        fontSize: 'clamp(32px, 4vw, 48px)', 
                        color: '#FAF7F0', 
                        fontWeight: 300, 
                        lineHeight: 1.3,
                        textAlign: 'center',
                        margin: 0,
                        letterSpacing: '-0.5px'
                      }}>
                        {swaras[activeStage].desc}
                      </h3>
                    </>
                  ) : (
                    <div style={{ position: 'relative', width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: 800 }}>
                      {[0, 1, 2].map((i) => (
                        <motion.div
                          key={`ring-${i}`}
                          animate={{
                            scale: [0, 2.5],
                            opacity: [0.8, 0],
                            rotateX: [60, 60],
                            rotateZ: [0, 180]
                          }}
                          transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: "easeOut",
                            delay: i * 1
                          }}
                          style={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            border: '1px solid rgba(200, 146, 42, 0.5)',
                            borderRadius: '50%',
                            boxShadow: '0 0 20px rgba(200, 146, 42, 0.2) inset'
                          }}
                        />
                      ))}
                      <motion.div 
                        animate={{ scale: [0.9, 1.1, 0.9], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        style={{ width: 12, height: 12, borderRadius: '50%', background: '#C8922A', boxShadow: '0 0 20px #C8922A' }}
                      />
                    </div>
                  )}

                  {/* Functional CTA Button */}
                  {isValidStage && (
                    <motion.a 
                      href={swaras[activeStage].link}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4, duration: 0.6 }}
                      className="btn-liquid" 
                      style={{ pointerEvents: 'auto', marginTop: 12, padding: '12px 32px', fontSize: 11 }}
                      data-cursor="CLICK"
                    >
                      {swaras[activeStage].cta}
                    </motion.a>
                  )}

                </motion.div>
              );
            })()}
          </AnimatePresence>
        </div>

      </div>
    </div>
  );
}

/* ── Individual Floating Letter ── */
function FloatSwara({ swara, progress, start, end, isActive }) {
  // Stable random peak opacity — computed once per mount, not on every render
  const peakOpacity = useRef(Math.random() * 0.4 + 0.6).current;

  // Map scroll progress to X and Y positions for smooth non-linear drifting
  const y = useTransform(progress, [start - 0.1, end], [swara.yStart, swara.yEnd]);
  const x = useTransform(progress, [start - 0.1, end], [swara.xStart, swara.xEnd]);
  const rotate = useTransform(progress, [start, end], [swara.rotStart || 0, swara.rotEnd || 180]);

  // Fade in and out around its active center
  const opacity = useTransform(
    progress,
    [start - 0.05, start + 0.05, end - 0.1, end],
    [0, peakOpacity, peakOpacity, 0]
  );
  
  const scale = useTransform(
    progress,
    [start, end],
    [0.3, 4] // Continues to grow as it passes camera
  );

  return (
    <motion.div
      style={{
        position: 'absolute',
        top: '50%', left: '50%',
        x, y, rotate, opacity, scale,
        color: swara.color,
        fontFamily: "'Cormorant Garamond',serif",
        fontSize: 'clamp(140px, 20vw, 300px)',
        fontWeight: 400,
        textShadow: `0 0 80px ${swara.color}44`,
        zIndex: isActive ? 5 : 2,
        transformStyle: 'preserve-3d',
        pointerEvents: 'none'
      }}
    >
      <span style={{
        display: 'block',
        transform: 'translate(-50%, -50%)',
      }}>
        {swara.hindi}
      </span>
    </motion.div>
  );
}

export default function Studio() {
  return (
    <>
      <StickyNarrative />
    </>
  );
}
